import { useCallback, useEffect, useState } from "react";
import { Menu, MenuUpdatePayload } from "src/business/restaurant/menu";
import { API_URL } from "src/Utils/API";
import { captureError } from "src/monitoring/sentry";

const buildMenuQueryKey = (restaurantId: string) =>
  `${API_URL}/restaurant/${restaurantId}/menu`;

export const useGetMenu = (restaurantId: string) => {
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState<Menu>([]);

  const getMenu = useCallback(async () => {
    setLoading(true);

    await fetch(buildMenuQueryKey(restaurantId), {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((response: Menu) => {
        setMenu(response);
      })
      .catch((e) => captureError(e))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  useEffect(() => {
    getMenu();
  }, [getMenu]);

  return { loading, menu, refetch: getMenu };
};

export const useUpdateMenu = (restaurantId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const updateMenu = async (menuData: MenuUpdatePayload) => {
    setLoading(true);
    setError(false);

    const response = await fetch(buildMenuQueryKey(restaurantId), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(menuData),
    })
      .then((response) => {
        console.log("response: ", response);

        if (!response.ok) {
          setError(true);

          return false;
        }

        return true;
      })
      .catch((e) => {
        captureError(e);
        setError(true);
        return false;
      })
      .finally(() => {
        setLoading(false);
      });

    return response;
  };

  return {
    updateMenu,
    loading,
    error,
    reset: () => {
      setError(false);
      setUploadSuccess(false);
      setLoading(false);
    },
    uploadSuccess,
  };
};

export const useDeleteMenuItem = (restaurantId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);

  const deleteMenuItem = async (itemId: string) => {
    setLoading(true);
    setError(false);

    const response = await fetch(
      `${buildMenuQueryKey(restaurantId)}/${itemId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    )
      .then((response) => {
        if (!response.ok) {
          setError(true);

          return false;
        }

        return true;
      })
      .catch((e) => {
        captureError(e);
        setError(true);
        return false;
      })
      .finally(() => {
        setLoading(false);
      });

    return response;
  };

  return { deleteMenuItem, loading, error, reset: () => setError(false) };
};
